import {
  FmlxButton,
  FmlxDatePicker,
  FmlxDropdown,
  FmlxIcon,
  FmlxTextBox,
} from 'fmlx-common-ui';
import React, { useEffect, useState } from 'react';
import {
  ActionProps,
  ActionWithRulesAndAddersProps,
  ActionWithRulesProps,
  CombinatorSelectorProps,
  FieldSelectorProps,
  OperatorSelectorProps,
  RuleGroupArray,
  RuleType,
  ValueEditorProps,
} from 'react-querybuilder';
import { FilterModalItem, AddFilterModal } from './AddFilterModal';
import { getFieldLabel } from './utils';
import { EConstraintOverrideType } from '../constants';

export const CombinatorDropdown = (
  props: React.PropsWithChildren<CombinatorSelectorProps>,
) => {
  const { value, handleOnChange, path } = props;

  const options = [
    {
      id: 0,
      text: 'AND',
      group: '',
      disabled: false,
      value: 'and',
    },
    {
      id: 1,
      text: 'OR',
      group: '',
      disabled: false,
      value: 'or',
    },
  ];

  const selectedCombinator = options.find((o) => o.value === value) ?? options[0];

  const handleCombinatorChange = (index: number) => {
    const selected = options[index];
    if (!selected || selected.disabled) {
      return;
    }

    handleOnChange(selected.value);
  };

  return (
    <div className="flex flex-row h-0 items-center gap-x-2">
      <div className="text-[0.8rem] text-nowrap">
        {path.length ? 'Group' : 'Apply Filter with'}
      </div>
      <FmlxDropdown
        id={`combinatordropdown-dropdown`}
        size={FmlxDropdown.Size.SMALL}
        menuProps={undefined}
        menuPosition={'default'}
        items={options}
        selectedItem={selectedCombinator.id}
        onChange={handleCombinatorChange}
      />
    </div>
  );
};

export const AddRulesButton = (props: ActionWithRulesAndAddersProps) => {
  const { path, context, disabled } = props;
  const { onAddQuery, basicFilters, advancedFilters, existingBasicFilterIds } = context;

  const [openModal, setOpenModal] = useState<boolean>(false);

  const onModalConfirm = (selectedFields: FilterModalItem[]) => {
    onAddQuery(selectedFields, path);
    setOpenModal(false);
  };

  const onModalClose = () => {
    setOpenModal(false);
    return;
  };

  const onClick = (e: React.SyntheticEvent) => {
    setOpenModal(true);
  };

  return (
    <>
      <FmlxButton
        id={`addrulesbutton-fmlxbutton`}
        type={FmlxButton.Type.POSITIVE}
        size={FmlxButton.Size.SMALL}
        withIcon={FmlxButton.WithIcon.START}
        icon={<FmlxIcon name="Add" customColor="white" />}
        disabled={disabled}
        label={'CONDITION'}
        onClick={onClick}
      />
      <AddFilterModal
        id={`addrulesbutton-addfiltermodal`}
        open={openModal}
        basicFilters={basicFilters}
        advancedFilters={advancedFilters}
        existingBasicFilterIds={existingBasicFilterIds}
        onCancel={onModalClose}
        onConfirm={onModalConfirm}
      />
    </>
  );
};

export const AddGroupButton = (props: ActionWithRulesAndAddersProps) => {
  return (
    <div className={props.className}>
      <FmlxButton
        id={`addgroupbutton-fmlxbutton`}
        type={FmlxButton.Type.POSITIVE}
        size={FmlxButton.Size.SMALL}
        withIcon={FmlxButton.WithIcon.START}
        icon={<FmlxIcon name="Add" customColor="white" />}
        disabled={props.disabled}
        label={'GROUP'}
        onClick={(e) => props.handleOnClick(e as React.MouseEvent)}
      />
    </div>
  );
};

type RemoveButtonProps = ActionProps | ActionWithRulesProps;

export const RemoveButton = (props: RemoveButtonProps) => {
  return (
    <div className={props.className}>
      <FmlxButton
        id={`removebutton-fmlxbutton`}
        size={FmlxButton.Size.SMALL}
        type={FmlxButton.Type.DANGER}
        variant={FmlxButton.Variant.PLAIN}
        icon={<FmlxIcon name={'CancelOutline'} customColor={'red'} />}
        onlyIcon
        onClick={props.handleOnClick}
        withTooltip={false}
      />
    </div>
  );
};

export const FixedFieldText = (props: FieldSelectorProps) => {
  const { options, value, className } = props;
  const items = options.flatMap((group) =>
    group.options.map((opt, index) => ({
      id: index,
      text: opt.label ?? opt.name,
      group: group.label ?? group.name,
      disabled: opt.disabled,
      value: opt.name,
    })),
  );

  const selected = items.find((i) => i.value == value) ?? items[0];
  const label =
    getFieldLabel(selected.text, (props.rule as any)?.isDropdown ?? false) ??
    selected.text ??
    '';

  return (
    <div className={`${className} pointer-events-none`}>
      <FmlxTextBox
        id={`fixedfieldtext-fmlxtextbox`}
        mode={FmlxTextBox.Mode.TEXT}
        value={label}
        onChange={undefined} // fixed value
        variant={FmlxTextBox.Variant.STANDARD}
        onClick={undefined}
        onEnterKeyPressed={undefined}
        onBlur={undefined}
      />
    </div>
  );
};

export const OperatorDropdown = (props: OperatorSelectorProps) => {
  const { options, value, handleOnChange, className, rule } = props;
  if (options.length <= 1) {
    return null;
  }

  // HACK: Ideally should change the rule to use a custom ruleType with our own extra attributes added
  if ((rule as any).isDropdown === true) {
    return (
      <div className={`${className} pointer-events-none`}>
        <FmlxTextBox
          id={`operatordropdown-fmlxtextbox`}
          mode={FmlxTextBox.Mode.TEXT}
          value={'='}
          onChange={undefined} // fixed value
          variant={FmlxTextBox.Variant.STANDARD}
          onClick={undefined}
          onEnterKeyPressed={undefined}
          onBlur={undefined}
        />
      </div>
    );
  }

  const items = options.map((opt, index) => ({
    id: index,
    text: opt.label ?? opt.name,
    group: '',
    disabled: opt.disabled,
    value: opt.name,
  }));

  const selected = items.find((i) => i.value === value) ?? items[0];

  const handleChange = (index: number) => {
    const selectedItem = items[index];
    if (!selectedItem || selectedItem.disabled) {
      return;
    }

    handleOnChange(selectedItem.value);
  };

  return (
    <div className={className}>
      <FmlxDropdown
        id={`operatordropdown-fmlxdropdown`}
        items={items}
        selectedItem={selected.id}
        onChange={handleChange}
        menuPosition={'default'}
        menuProps={undefined}
      />
    </div>
  );
};

function findNearestScoreGroup(
  path: number[],
  scoreGroupFieldId: number,
  rules?: RuleGroupArray,
): string | undefined {
  if (!rules) {
    return;
  }

  const getRuleGroupAtPath = (path: number[]): RuleGroupArray | undefined => {
    let current: RuleGroupArray = rules;

    for (const index of path) {
      const item = current.at(index);
      if (!item || !('combinator' in item)) {
        return;
      }
      current = item.rules;
    }

    return current;
  };

  for (let depth = path.length; depth >= 0; depth--) {
    const ruleGroup = getRuleGroupAtPath(path.slice(0, depth));
    if (!ruleGroup) {
      continue;
    }

    const match = ruleGroup.find(
      (r): r is RuleType => !('combinator' in r) && Number(r.field) === scoreGroupFieldId,
    );

    if (match) {
      return match.value;
    }
  }

  return;
}

const generateIdFromPathAndField = (path: number[], field: string) => {
  return `${path.toString()}${field}`;
};

export type BasicFilterFieldOverride =
  | { type: EConstraintOverrideType.Number; unit: string }
  | { type: EConstraintOverrideType.Boolean };

export type FilterOptions = {
  id: number;
  text: string;
  value: string;
  group: string;
  disabled?: boolean;
};

export type FilterPresetValues = {
  values: FilterOptions[];
  defaultValue?: string;
  disabled?: boolean;
};

export const ValueInput = (props: ValueEditorProps) => {
  const {
    value,
    field,
    handleOnChange,
    inputType,
    className,
    operator,
    path,
    context,
    rule,
  } = props;
  const { basicFilterFields, basicFilterPresetValues, queryRules } = context;

  if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
    return;
  }

  const fieldId = Number(field);
  const preset = basicFilterPresetValues?.[fieldId];

  // HACK: Ideally should change the rule to use a custom ruleType with our own extra attributes added
  const isDropdownField = (rule as any).isDropdown === true;
  const fieldOverride =
    context?.basicFilterFieldOverrides?.[(rule as any).constraintType];

  if (isDropdownField && fieldOverride) {
    if (fieldOverride.type === EConstraintOverrideType.Number) {
      return (
        <div className={className}>
          <FmlxTextBox
            id={`valueinput-${field}-override-fmlxtextbox`}
            mode="number"
            variant={FmlxTextBox.Variant.OUTLINE}
            decoration="unit"
            unit={fieldOverride.unit}
            value={value ?? ''}
            onChange={(e) => handleOnChange(e.value)}
          />
        </div>
      );
    }
  }

  if (!isDropdownField) {
    return (
      <ValueInputTextbox
        id={`valueinput-${field}-valueinputtextbox`}
        value={value}
        inputType={inputType}
        className={className}
        path={path}
        field={field}
        onChange={handleOnChange}
      />
    );
  }
  if (basicFilterFields.scoreField?.id && fieldId === basicFilterFields.scoreField?.id) {
    return (
      <ValueInputDropdownScore
        id={`valueinput-${field}-valueinputdropdownscore`}
        value={value}
        className={className}
        path={path}
        preset={preset}
        basicFilterFields={basicFilterFields}
        currentRules={queryRules}
        onChange={handleOnChange}
      />
    );
  }
  return (
    <ValueInputDropdown
      id={`valueinput-${field}-valueinputdropdown`}
      value={value}
      className={className}
      path={path}
      preset={preset}
      basicFilterFields={basicFilterFields}
      currentRules={queryRules}
      onChange={handleOnChange}
    />
  );
};

type ValueInputTextboxProps = {
  id: string;
  value: any;
  inputType?: string;
  className?: string;
  path: number[];
  field: string;
  onChange: (value: any) => void;
};

const ValueInputTextbox = ({
  id,
  value,
  inputType,
  className,
  path,
  field,
  onChange,
}: ValueInputTextboxProps) => {
  if (inputType === 'date') {
    return (
      <div className={className}>
        <FmlxDatePicker
          id={`${id}-fmlxdatepicker`}
          variant={FmlxDatePicker.Variant.DATE}
          value={value ?? 0}
          onChange={(e, value) => onChange(value)}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <FmlxTextBox
        id={`${id}-fmlxtextbox`}
        key={generateIdFromPathAndField(path, field)}
        mode={inputType === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={(e) => onChange(e.value)}
        variant={FmlxTextBox.Variant.OUTLINE}
      />
    </div>
  );
};

type ValueInputDropdownProps = {
  id: string;
  value: any;
  className?: string;
  path: number[];
  preset: { values: FilterOptions[] };
  basicFilterFields: any;
  currentRules: any;
  onChange: (value: any) => void;
};

const ValueInputDropdown = ({
  id,
  value,
  className,
  path,
  preset,
  basicFilterFields,
  currentRules,
  onChange,
}: ValueInputDropdownProps) => {
  const options = preset?.values;

  const selected = options.find((o) => o.value === value)?.id ?? 0;

  const onValueChange = (index: number) => {
    onChange(options?.find((o) => o.id === index)?.value);
  };

  return (
    <div className={className}>
      <FmlxDropdown
        id={`${id}-fmlxdropdown`}
        items={options}
        selectedItem={selected}
        onChange={onValueChange}
        menuPosition={'default'}
        menuProps={undefined}
      />
    </div>
  );
};

// Special dropdown component for score, since it depends on scoreGroup
const ValueInputDropdownScore = ({
  id,
  value,
  className,
  path,
  preset,
  basicFilterFields,
  currentRules,
  onChange,
}: ValueInputDropdownProps) => {
  const scoreGroup = findNearestScoreGroup(
    [...path],
    basicFilterFields.scoreGroupField?.id,
    currentRules,
  );
  const options = preset.values.filter(
    (score) => score.group === scoreGroup || score.group === '',
  );

  const onValueChange = (index: number) => {
    onChange(options.find((o) => o.id === index)?.value);
  };

  useEffect(() => {
    if (!options.length) {
      return;
    }

    const isValid = options.some((o) => o.value === value);
    if (!isValid) {
      onValueChange(0);
    }
  }, [scoreGroup, options, value, onValueChange]);

  const selected = options.find((o) => o.value === value)?.id ?? 0;

  return (
    <div className={className}>
      <FmlxDropdown
        id={`${id}-fmlxdropdown`}
        items={options}
        selectedItem={selected}
        onChange={onValueChange}
        menuPosition={'default'}
        menuProps={undefined}
      />
    </div>
  );
};
